import React, { useEffect, useState } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { Button } from '@merels/ui'
import { useWebContainer } from '../providers/DepProvider/DepProvider'
import { 
  Key, 
  Check, 
  Copy, 
  AlertCircle, 
  Sparkles,
  ChevronRight,
  ArrowLeft
} from 'lucide-react'
import { buildAgentPrompt, buildPrBootstrapCommand } from './agentPrompt'
import { DevelopmentProgress, DevelopmentStatusCard } from '../development/DevelopmentStatus/DevelopmentStatus'

// Simple regex for kebab-case slug validation
const isSlug = (s: string) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s)

type WizardStep = 'intro' | 'publisher' | 'game-name' | 'review'

export function NewGameWizard() {
  useSignals()
  const container = useWebContainer()
  const { auth, roomConnection } = container
  const workerUrl = roomConnection._workerUrl || ''

  const isRoomOwner = container.shellVM.room.isOwner.value
  const isLocalMode = container.mode.isLocalMode()

  // State managers
  const [publishers, setPublishers] = useState<any[]>([])
  const [games, setGames] = useState<any[]>([])
  const [activeProject, setActiveProject] = useState<DevelopmentProgress | null>(null)
  
  const [isCreatingPublisher, setIsCreatingPublisher] = useState(false)
  const [publisherSlug, setPublisherSlug] = useState('')
  const [publisherName, setPublisherName] = useState('')
  const [selectedPublisherSlug, setSelectedPublisherSlug] = useState('')

  const [gameSlug, setGameSlug] = useState('')
  const [gameName, setGameName] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invitationToken, setInvitationToken] = useState<string | null>(null)
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null)
  const [copied, setCopied] = useState<'bootstrap' | 'prompt' | null>(null)

  // Step state
  const [step, setStep] = useState<WizardStep>('intro')

  // Fetch owned publishers & games to check for slug uniqueness
  const loadCatalog = async () => {
    if (!auth.currentUser.value) return
    setError(null)
    try {
      const token = await auth.getToken()
      const headers: HeadersInit = {}
      if (token) headers['Authorization'] = `Bearer ${token}`
      if (window.location.hostname.endsWith('.localhost')) {
        headers['x-local-worktree'] = window.location.hostname.split('.')[0]
      }

      const res = await fetch(`${workerUrl}/api/creator/admin/catalog`, { headers })
      const data = await res.json()
      if (res.ok && data.success) {
        setPublishers(data.publishers || [])
        setGames(data.games || [])
        if (data.publishers && data.publishers.length > 0) {
          setSelectedPublisherSlug(data.publishers[0].slug)
          setIsCreatingPublisher(false)
        } else {
          setIsCreatingPublisher(true)
        }
      }
    } catch (err: any) {
      console.error('[NewGameWizard] Failed to load catalog:', err)
    }
  }

  /** Refreshes one project's lifecycle without reloading the full admin catalog. */
  const loadProjectProgress = async (projectId: string) => {
    if (!auth.currentUser.value) return
    try {
      const token = await auth.getToken()
      const headers: HeadersInit = {}
      if (token) headers['Authorization'] = `Bearer ${token}`
      if (window.location.hostname.endsWith('.localhost')) {
        headers['x-local-worktree'] = window.location.hostname.split('.')[0]
      }

      const response = await fetch(`${workerUrl}/api/creator/projects/${encodeURIComponent(projectId)}`, { headers })
      const data = await response.json()
      if (!response.ok || !data.success || !data.progress) return
      setActiveProject(data.progress)
    } catch (err: any) {
      console.error('[NewGameWizard] Failed to load project progress:', err)
    }
  }

  useEffect(() => {
    if (auth.currentUser.value) {
      void loadCatalog()
    }
  }, [auth.currentUser.value])

  // Keep the creator-facing lifecycle current while the agent works from the invitation.
  useEffect(() => {
    if (!invitationToken || !createdProjectId) return
    const refresh = window.setInterval(() => {
      void loadProjectProgress(createdProjectId)
      void container.gameRegistry.refreshCatalog?.()
    }, 10_000)
    return () => window.clearInterval(refresh)
  }, [invitationToken, createdProjectId, container.gameRegistry])

  // Derive slugs automatically as user types names
  const handleGameNameChange = (val: string) => {
    setGameName(val)
    const derived = val
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    setGameSlug(derived)
  }

  const handlePublisherNameChange = (val: string) => {
    setPublisherName(val)
    const derived = val
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    setPublisherSlug(derived)
  }

  const handleNextFromIntro = () => {
    setStep('publisher')
  }

  const handleNextFromPublisher = () => {
    const finalPubSlug = isCreatingPublisher ? publisherSlug : selectedPublisherSlug

    if (isCreatingPublisher) {
      if (!publisherName.trim()) {
        setError('Publisher display brand name is required.')
        return
      }
      if (!publisherSlug || !isSlug(publisherSlug)) {
        setError('Publisher brand ID (slug) must be lower-case kebab-case.')
        return
      }
      const isPublisherSlugTaken = publishers.some(p => p.slug === publisherSlug)
      if (isPublisherSlugTaken) {
        setError(`The publisher brand ID "${publisherSlug}" is already in use. Please choose a different one.`)
        return
      }
    } else {
      if (!finalPubSlug) {
        setError('Please select a publisher brand.')
        return
      }
    }
    setError(null)
    setStep('game-name')
  }

  const handleNextFromGameName = () => {
    if (!gameName.trim()) {
      setError('Please provide a name for your game.')
      return
    }
    setError(null)
    setStep('review')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const finalPubSlug = isCreatingPublisher ? publisherSlug : selectedPublisherSlug
    const finalPubName = isCreatingPublisher ? publisherName : (publishers.find(p => p.slug === selectedPublisherSlug)?.displayName || '')

    if (!gameSlug || !isSlug(gameSlug)) {
      setError('Game slug must be lower-case kebab-case (letters, numbers, hyphens).')
      return
    }

    const slugExists = games.some(
      (g) => g.gameSlug === gameSlug && g.publisherSlug === finalPubSlug
    )
    if (slugExists) {
      setError(`A game with the slug "${gameSlug}" already exists under publisher "${finalPubSlug}".`)
      return
    }

    setSubmitting(true)
    try {
      const token = await auth.getToken()
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) headers['Authorization'] = `Bearer ${token}`
      if (window.location.hostname.endsWith('.localhost')) {
        headers['x-local-worktree'] = window.location.hostname.split('.')[0]
      }

      const res = await fetch(`${workerUrl}/api/creator/games/create-with-agent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          publisherSlug: finalPubSlug,
          publisherDisplayName: finalPubName,
          gameSlug,
          gameDisplayName: gameName
        })
      })

      const data = await res.json()
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to create game')
      }

      setCreatedProjectId(data.projectId)
      setInvitationToken(data.invitationToken)
      void loadProjectProgress(data.projectId)
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating the project.')
    } finally {
      setSubmitting(false)
    }
  }

  // Non-owner / observer view
  if (!isRoomOwner && !isLocalMode) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 bg-[#0a0b0d] text-center font-sans">
        <div className="max-w-sm space-y-4">
          <div className="relative inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-6 w-6 animate-pulse" />
          </div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-100">Setup In Progress</h2>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            The room owner is currently bootstrapping a brand new game project. Please wait here for the agent invitation and workspace to be configured.
          </p>
        </div>
      </div>
    )
  }

  // Authenticate owner view
  if (!auth.currentUser.value) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 bg-[#0a0b0d] text-center font-sans">
        <div className="max-w-sm space-y-4 border border-[#1e212f] bg-[#0c0d13] p-6 rounded-lg shadow-xl">
          <div className="relative inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary font-bold">
            <Key className="h-6 w-6" />
          </div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-100">Authentication Required</h2>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            You must log in to register your publisher identity, generate security tokens, and invite your agent to the workspace.
          </p>
          <Button 
            onClick={() => auth.signIn().catch(console.error)} 
            className="w-full h-8 font-bold text-xs uppercase tracking-wider"
          >
            Log In to Continue
          </Button>
        </div>
      </div>
    )
  }

  // Success invitation screen ("Project Workspace Ready!")
  if (invitationToken) {
    const inviteUrl = `${window.location.origin}/i/${invitationToken}`
    const prNumber = import.meta.env.VITE_PR_NUMBER
    const promptText = buildAgentPrompt(inviteUrl, prNumber)
    const bootstrapCommand = prNumber ? buildPrBootstrapCommand(invitationToken, prNumber) : null
    
    return (
      <div className="w-full h-full bg-[#0a0b0d] overflow-y-auto flex flex-col font-sans">
        <div className="flex-1 flex flex-col justify-center items-center p-8">
          <div className="max-w-md w-full space-y-6">
            <div className="flex items-center space-x-3 text-emerald-400">
              <div className="h-9 w-9 flex items-center justify-center rounded-full bg-emerald-950/50 shrink-0">
                <Check className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-400">Game Registered!</h2>
              </div>
            </div>

            <div className="space-y-4 text-xs text-gray-200">
              <ol className="list-decimal space-y-3 pl-5 leading-relaxed">
                <li>Create an empty folder for this game and open a terminal in that folder.</li>
                {bootstrapCommand ? (
                  <li>
                    Connect that folder to this PR preview before starting your agent:
                    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded border border-[#272b3f] bg-[#10121a] p-3 font-mono text-[10px] text-gray-300">{bootstrapCommand}</pre>
                    <Button
                      onClick={() => {
                        void navigator.clipboard.writeText(bootstrapCommand)
                        setCopied('bootstrap')
                        setTimeout(() => setCopied(null), 2000)
                      }}
                      variant="outline"
                      className="mt-2 h-8 px-3 text-[10px] font-bold"
                    >
                      {copied === 'bootstrap' ? 'Copied!' : 'Copy Bootstrap Command'}
                    </Button>
                  </li>
                ) : null}
                <li>Start your coding agent from inside that folder.</li>
                <li>Paste the prompt below into the agent.</li>
              </ol>

              <blockquote className="border-l-4 border-emerald-500 pl-4 py-1 text-gray-300 text-sm leading-relaxed italic relative select-all whitespace-pre-wrap">
                {promptText}
              </blockquote>

              <div className="flex justify-start">
                <Button 
                  onClick={() => {
                    void navigator.clipboard.writeText(promptText)
                    setCopied('prompt')
                    setTimeout(() => setCopied(null), 2000)
                  }} 
                  className="h-9 px-4 flex items-center space-x-1.5"
                >
                  {copied === 'prompt' ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                  <span className="font-bold">{copied === 'prompt' ? 'Copied!' : 'Copy Prompt'}</span>
                </Button>
              </div>

              <p className="text-muted-foreground leading-relaxed pt-1">
                You do not need to provide your agent with the details up front. Let them ask you questions about your game later.
              </p>

              {activeProject && <DevelopmentStatusCard progress={activeProject} />}
              <p className="text-[10px] text-muted-foreground">
                This status updates as the agent completes checkpoints. After the first publish, you can open it from the badge on the game in your catalog.
              </p>
            </div>

            <div className="pt-4 border-t border-[#1e212f] flex justify-end">
              <Button 
                onClick={() => {
                  void container.gameRegistry.refreshCatalog?.()
                  void container.roomActions.endGame()
                }}
                variant="outline"
                className="h-8 px-4 text-xs font-semibold"
              >
                Exit Wizard
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full bg-[#0a0b0d] overflow-y-auto flex flex-col font-sans text-xs text-gray-200">
      {/* Hero image banner spanning the full width of play area (shown on Intro step) */}
      {step === 'intro' && (
        <div className="w-full relative h-[200px] md:h-[260px] overflow-hidden bg-[#12141c] shrink-0">
          <img 
            src="/agent-banner.png" 
            alt="Coding Agents Building Board Games" 
            className="w-full h-full object-cover select-none"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0d] via-transparent to-transparent" />
        </div>
      )}

      {/* Main wizard layout */}
      <div className="flex-1 flex flex-col justify-start py-8 px-6">
        <div className={step === 'intro' ? 'max-w-2xl mx-auto w-full space-y-6' : 'max-w-md mx-auto w-full space-y-6'}>
          {error && (
            <div className="rounded-md border border-red-900/40 bg-red-950/10 p-3 text-red-400 flex items-center space-x-2 text-[10px]">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* 1. INTRO STEP */}
          {step === 'intro' && (
            <div className="space-y-6 text-center sm:text-left">
              <p className="text-sm text-gray-200 leading-relaxed">
                You can create your own Merels game using a coding agent, like Codex or Claude! Let us walk you through the steps.
              </p>

              <div className="pt-3">
                <Button
                  onClick={handleNextFromIntro}
                  className="w-full h-9 font-bold text-xs uppercase tracking-wider flex items-center justify-center space-x-1.5"
                >
                  <span>Let's Get Started</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* 2. PUBLISHER BRAND STEP */}
          {step === 'publisher' && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <h2 className="text-sm font-bold text-gray-100">
                  {publishers.length > 0 && !isCreatingPublisher
                    ? 'Which publisher do you want to use for this game?'
                    : 'What do you want your game publishing brand name to be?'}
                </h2>
              </div>

              <div className="space-y-3">
                {publishers.length > 0 && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setIsCreatingPublisher(!isCreatingPublisher)}
                      className="text-[10px] text-primary font-semibold hover:underline"
                    >
                      {isCreatingPublisher ? 'Use Existing Brand' : 'Create New Brand'}
                    </button>
                  </div>
                )}

                {isCreatingPublisher ? (
                  <div className="space-y-3 p-3 rounded border border-[#222536] bg-[#10121a]">
                    <div className="space-y-1">
                      <label className="text-[9px] text-gray-400 uppercase font-bold">Brand Name</label>
                      <input
                        type="text"
                        autoFocus
                        placeholder="e.g. Cool Games"
                        value={publisherName}
                        onChange={(e) => handlePublisherNameChange(e.target.value)}
                        className="w-full h-8 px-2.5 rounded bg-[#151722] border border-[#272b3f] text-gray-200 text-xs focus:outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] text-gray-400 uppercase font-bold">Brand ID (Slug)</label>
                      <input
                        type="text"
                        placeholder="e.g. cool-games"
                        value={publisherSlug}
                        onChange={(e) => setPublisherSlug(e.target.value)}
                        className="w-full h-8 px-2.5 rounded bg-[#151722] border border-[#272b3f] text-gray-200 font-mono text-xs focus:outline-none"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <label className="text-[9px] text-gray-400 uppercase font-bold">Select Publishing Brand</label>
                    <select
                      value={selectedPublisherSlug}
                      onChange={(e) => setSelectedPublisherSlug(e.target.value)}
                      className="w-full h-8 px-2.5 rounded bg-[#151722] border border-[#272b3f] text-gray-200 text-xs focus:outline-none"
                    >
                      {publishers.map((pub) => (
                        <option key={pub.publisherId} value={pub.slug}>
                          {pub.displayName} ({pub.slug})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex space-x-2 pt-2">
                <Button
                  onClick={() => { setError(null); setStep('intro'); }}
                  variant="outline"
                  className="w-24 h-8 text-xs flex items-center justify-center space-x-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span>Back</span>
                </Button>
                <Button
                  onClick={handleNextFromPublisher}
                  className="flex-1 h-8 font-bold text-xs uppercase tracking-wider flex items-center justify-center space-x-1"
                >
                  <span>Continue</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* 3. GAME NAME STEP */}
          {step === 'game-name' && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <h2 className="text-sm font-bold text-gray-100">What do you want to call your game?</h2>
                <p className="text-[10px] text-muted-foreground">
                  You can change this later.
                </p>
              </div>

              <div className="space-y-2">
                <input
                  type="text"
                  autoFocus
                  placeholder="e.g. Ultimate Tic Tac Toe"
                  value={gameName}
                  onChange={(e) => handleGameNameChange(e.target.value)}
                  className="w-full h-9 px-3 rounded bg-[#151722] border border-[#272b3f] text-gray-200 text-xs focus:outline-none font-sans"
                />
              </div>

              <div className="flex space-x-2 pt-2">
                <Button
                  onClick={() => { setError(null); setStep('publisher'); }}
                  variant="outline"
                  className="w-24 h-8 text-xs flex items-center justify-center space-x-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span>Back</span>
                </Button>
                <Button
                  onClick={handleNextFromGameName}
                  className="flex-1 h-8 font-bold text-xs uppercase tracking-wider flex items-center justify-center space-x-1"
                >
                  <span>Continue</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* 4. REVIEW STEP */}
          {step === 'review' && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <h2 className="text-sm font-bold text-gray-100">Review & Create Game</h2>
                <p className="text-[10px] text-muted-foreground">
                  Change any configuration details below before generating the agent invitation.
                </p>
              </div>

              <div className="space-y-3 border border-[#1e212f] bg-[#12141c] rounded p-4 font-sans text-xs">
                <div className="space-y-1">
                  <label className="text-[9px] text-gray-400 uppercase font-bold tracking-wider">Game Name</label>
                  <input
                    type="text"
                    value={gameName}
                    onChange={(e) => handleGameNameChange(e.target.value)}
                    className="w-full h-8 px-2.5 rounded bg-[#151722] border border-[#272b3f] text-gray-200 text-xs focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] text-gray-400 uppercase font-bold tracking-wider">Game Slug (ID)</label>
                  <input
                    type="text"
                    value={gameSlug}
                    onChange={(e) => setGameSlug(e.target.value)}
                    className="w-full h-8 px-2.5 rounded bg-[#151722] border border-[#272b3f] text-gray-200 font-mono text-xs focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-y-2.5 text-[11px] leading-relaxed pt-3 border-t border-[#1e212f]/50 mt-2">
                  <div>
                    <span className="text-muted-foreground font-bold text-[9px] uppercase tracking-wider block">Publisher Brand</span>
                    <span className="text-gray-200 font-semibold">
                      {isCreatingPublisher ? publisherName : (publishers.find(p => p.slug === selectedPublisherSlug)?.displayName || '')}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-bold text-[9px] uppercase tracking-wider block">Publisher Slug</span>
                    <span className="text-gray-200 font-mono text-[10px]">{isCreatingPublisher ? publisherSlug : selectedPublisherSlug}</span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-2 pt-2">
                <Button
                  disabled={submitting}
                  onClick={() => { setError(null); setStep('game-name'); }}
                  variant="outline"
                  className="w-24 h-8 text-xs flex items-center justify-center space-x-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span>Back</span>
                </Button>
                <Button
                  disabled={submitting}
                  onClick={handleSubmit}
                  className="flex-1 h-8 font-bold text-xs uppercase tracking-wider flex items-center justify-center space-x-1"
                >
                  {submitting ? (
                    <>
                      <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent mr-1" />
                      <span>Creating Project...</span>
                    </>
                  ) : (
                    <>
                      <span>Create Game & Invite Agent</span>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
